import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';

/** Bulk delete confirm dialog with cascade counts.
 * @param {object} info  { counts, names, ids } returned by the /bulk-delete-preview endpoint
 * @param {string} entityLabel  singular entity name, e.g. "proposal"
 * @param {(ids:string[]) => Promise<any>} onConfirm  — returns the delete API call
 */
export default function BulkDeleteDialog({ open, onClose, info, entityLabel, onConfirm, testId = 'bulk-delete-dialog' }) {
    const [deleting, setDeleting] = useState(false);
    if (!info) return null;

    const primaryCount = info.counts?.[entityLabel + 's'] ?? info.counts?.[entityLabel] ?? info.ids?.length ?? 0;

    const submit = async () => {
        setDeleting(true);
        try { await onConfirm(info.ids); onClose(); }
        finally { setDeleting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !deleting && onClose()}>
            <DialogContent className="max-w-[520px]" data-testid={testId}>
                <DialogHeader>
                    <DialogTitle className="font-display text-[19px] text-red-700">
                        Permanently delete {primaryCount} {entityLabel}{primaryCount === 1 ? '' : 's'}?
                    </DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        This is irreversible. Everything linked to these records will be hard-deleted from the database.
                    </DialogDescription>
                </DialogHeader>
                {info.names?.length > 0 && (
                    <div className="text-[12.5px] text-[color:var(--bm-text)]">
                        <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1.5 capitalize">{entityLabel}s</div>
                        <ul className="list-disc pl-5 space-y-0.5 text-[12.5px] text-[color:var(--bm-text-muted)] max-h-[120px] overflow-y-auto">
                            {info.names.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                    </div>
                )}
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-1.5">Cascade — will also delete</div>
                    <div className="grid grid-cols-2 gap-2 text-[12.5px]">
                        {Object.entries(info.counts || {})
                            .filter(([k]) => k !== (entityLabel + 's') && k !== entityLabel)
                            .map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between bg-[color:var(--bm-bg-mute)] rounded-md px-3 py-1.5">
                                    <span className="capitalize text-[color:var(--bm-text-muted)]">{k}</span>
                                    <span className="font-semibold text-[color:var(--bm-text)]">{v}</span>
                                </div>
                            ))}
                    </div>
                </div>
                <DialogFooter className="pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={deleting}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid={`${testId}-confirm`}
                    >
                        {deleting ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
